import { useMutation, useQuery } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import FormSection from '@components/FormSection';
import InputFieldControlled from '@components/InputFieldControlled';
import InputRichText from '@components/InputRichText';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import { sanitizeHtml } from '@helper/string.utils';
import { UPDATE_BRAND } from '@gql/brand';
import { GET_SECTORS } from '@gql/sectors';
import { toUploadFileInput, toUploadFilesInput } from '@modules/brands/utils';
import { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { AutoCompleteField } from '@components/AutoComplete/AutoCompleteField';
import { useOptionsContext } from '@modules/talents/context/options-context';
import InputMultiSelect from '@components/InputMultiSelect';

type BrandAboutCardProps = {
    brand: {
        _id?: string;
        brandName?: string;
        brandBanner?: { _id?: string; url?: string };
        brandLogo?: { _id?: string; url?: string };
        brandPhotos?: Array<{ _id?: string; url?: string; name?: string; size?: number; mimetype?: string }>;
        brandVideos?: Array<{ _id?: string; url?: string; name?: string; size?: number; mimetype?: string }>;
        sector?: { _id?: string; sectorName?: string };
        pro?: { commercialName?: string };
        description?: string;
        isFeatured?: boolean;
    };
    disabled?: boolean;
    onSuccess?: () => void;
};

const BrandAboutCard = ({ brand, disabled, onSuccess }: BrandAboutCardProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    const { data: optionsData } = useOptionsContext();

    const sectorOptions = useMemo(
        () => optionsData?.options?.sectors?.[0]?.children?.map(item => ({ label: item.name, value: item.id })) || [],
        [optionsData],
    );
    const { control, handleSubmit, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            brandName: brand.brandName ?? '',
            description: brand.description ?? '',
            sector: brand.sector?._id ?? null,
        },
    });

    const [update, { loading }] = useMutation(UPDATE_BRAND, {
        onError: err => {
            enqueueSnackbar(err.message, { variant: 'error' });
        },
        onCompleted: () => {
            enqueueSnackbar('Marque mise à jour avec succès', { variant: 'success' });
            onSuccess?.();
        },
    });

    const submitHandler = async (data: { brandName: string; description: string; sector: string | null }) => {
        await update({
            variables: {
                id: brand._id,
                brandInput: {
                    brandName: data.brandName,
                    description: data.description,
                    sector: data.sector ?? undefined,
                    brandLogo: toUploadFileInput(brand.brandLogo ?? null),
                    brandBanner: toUploadFileInput(brand.brandBanner ?? null),
                    brandPhotos: toUploadFilesInput(brand.brandPhotos ?? []),
                    brandVideos: toUploadFilesInput(brand.brandVideos ?? []),
                },
            },
        });
        setFormState('view');
    };

    return (
        <FormContainer
            onBack={() => { }}
            formTitle="À propos de la marque"
            formDescription="Nom, secteur et description de la marque."
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            hideEdit={disabled || !brand._id}
            onCancel={() => {
                reset();
            }}
            onSubmit={handleSubmit(submitHandler)}
            submitText="Enregistrer"
        >
            <Stack spacing={2}>
                <InputFieldControlled
                    label="Nom de la marque"
                    placeholder="Nom de la marque"
                    name="brandName"
                    control={control}
                    required
                    viewOnly={formState === 'view'}
                />
                 <InputMultiSelect
                        viewOnly={formState === 'view'}
                        single
                        control={control}
                        name="sector"
                        label="Secteur d'activité"
                        placeholder="Choisissez votre secteur"
                        options={sectorOptions}
                        required
                    />
                <Stack style={{ width: '100%', position: 'relative' }}>
                    <InputRichText
                        label="Description"
                        name="description"
                        placeholder="Décrivez la marque pour attirer les futurs talents."
                        fullWidth
                        control={control}
                        defaultValue={undefined}
                        maxLength={1000}
                        viewOnly={formState === 'view'}
                    />
                </Stack>
            </Stack>
        </FormContainer>
    );
};

export default BrandAboutCard;
